import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';
import catPoster from '../../../assets/images/nano-escape-alert-cat-poster.jpg';
import catVideo from '../../../assets/images/nano-escape-alert-cat.mp4';
import dogPoster from '../../../assets/images/nano-escape-alert-dog-poster.jpg';
import dogVideo from '../../../assets/images/nano-escape-alert-dog.mp4';
import { gqlTypes } from '../../../types/gqlTypes';
import styles from './EscapeAlert.module.scss';

/**
 * Seconds into the clip at which the pet crosses the safe zone edge, and the frame the Nano offer
 * email's GIF drops its notification in.
 */
const ALERT_AT_SECONDS = 2.2;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

interface EscapeAlertProps {
  petName: string;
  species?: gqlTypes.Species | null;
  /** A caption set beside the clip on desktop. Hidden on a phone, where the clip has the panel alone. */
  children?: ReactNode;
}

/**
 * The escape-alert animation from the Nano offer emails (E1): the pet walks out of its safe zone and
 * the phone shows "{name} left Home".
 *
 * The email's GIF bakes its sample pet's name into that notification, which on a screen titled "Add
 * GPS tracking for Bella" would show Bella's owner an alert about Thor. So the clip here is the same
 * animation with the notification painted out, and the notification is drawn on top in markup with
 * the registrant's own pet, shown off the video's clock so it lands on the same frame every loop.
 *
 * With reduced motion the clip stays on its last frame, the pet outside the zone and the alert shown.
 */
export default function EscapeAlert({ petName, species, children }: EscapeAlertProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion] = useState(prefersReducedMotion);
  const [alertShown, setAlertShown] = useState(reducedMotion);
  const isCat = species === gqlTypes.Species.CAT;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) {
      return;
    }

    // muted on the node rather than as a prop: react 17 never writes the `muted` attribute (and warns
    // while trying), and ios refuses to autoplay a clip that is not muted, so set it and start it here
    video.muted = true;

    let cancelled = false;
    let frame = 0;
    const followClip = () => {
      if (!video.paused) {
        setAlertShown(video.currentTime >= ALERT_AT_SECONDS);
      }
      frame = requestAnimationFrame(followClip);
    };

    const start = () => {
      // older browsers (and jsdom) return nothing from play() rather than a promise
      const started: Promise<void> | undefined = video.play();
      if (started) {
        started.catch(() => {
          // autoplay refused (low power mode): the poster is the last frame, so show the alert to
          // match. guarded, because the promise can settle after the component has gone.
          if (!cancelled) {
            setAlertShown(true);
          }
        });
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(followClip);
    };

    const stop = () => {
      video.pause();
      cancelAnimationFrame(frame);
    };

    // plays only while on screen: the shopper spends most of checkout typing in the cards below it,
    // and a clip and a frame loop running out of sight there only cost battery
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
      };
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        start();
      } else {
        stop();
      }
    });
    observer.observe(video);

    return () => {
      cancelled = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [reducedMotion]);

  return (
    <div className={styles.frame}>
      <div className={styles.screen} role="img" aria-label={`${petName} walks out of home and Fi sends an alert`}>
        <video
          ref={videoRef}
          className={styles.video}
          src={isCat ? catVideo : dogVideo}
          poster={isCat ? catPoster : dogPoster}
          loop
          playsInline
          // with reduced motion the clip never plays, so only its poster is fetched
          preload={reducedMotion ? 'none' : 'auto'}
          aria-hidden="true"
        />
        <div className={classNames(styles.alert, { [styles.alertShown]: alertShown })} aria-hidden="true">
          <span className={styles.alertIcon}>fi</span>
          <span className={styles.alertText}>
            <span className={styles.alertTitleRow}>
              <span className={styles.alertTitle}>{petName} left Home</span>
              <span className={styles.alertTime}>now</span>
            </span>
            <span className={styles.alertBody}>Tap to see {petName}’s live location.</span>
          </span>
        </div>
      </div>
      {children ? <div className={styles.caption}>{children}</div> : null}
    </div>
  );
}
